import * as React from "react";
import { AIAgentTask, AiScriptChangeIteration, ExecuteFunctionResponse } from "../../../../../generated/api";
import { Button, Label, Modal, Segment, Message, Divider, Form, Grid, Icon } from "semantic-ui-react";
import AceEditor from "react-ace";
import {aiAgentTasks, triggerAiScriptTask, updateAiAgentTaskDirectly} from "./AiScripterPage";
import { v4 as uuid } from 'uuid';
import { notify } from 'react-notify-toast';
import {editAiAgentTaskIteration} from "./AiScripterPage";

export interface AiAgentTaskIterationModalProps {
    isOpen: boolean,
    onClose: (xReqId: string | null) => void,
    task: AIAgentTask & { cancelledByUser?: boolean },
    iteration: AiScriptChangeIteration,
    indexNr: number,
}

const scriptPreviewStateCache = new Map();

export function AiAgentTaskIterationModal(props: AiAgentTaskIterationModalProps) {
    const iterationId = `${props.task?._id}-${props.indexNr}`;
    const defaultScriptVisibility = false;


    const [userInput, setUserInput] = React.useState<string>("");
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [showScriptPreview, setShowScriptPreview] = React.useState<boolean>(defaultScriptVisibility);
    const [selectedOption, setSelectedOption] = React.useState<boolean | null>(null);
    const iteration = props.iteration;
    const executeFunc = iteration.aiModelResponseScripting?.aiModelResponse?.executeFunc;
    const script = iteration.aiModelResponseScripting?.aiModelResponse?.script || "";
    const query = iteration.aiModelResponseScripting?.aiModelResponse?.query || "";
    const scriptExecutionLogs = iteration.scriptExecutionLogs || "";

    const isScriptExecution = executeFunc === ExecuteFunctionResponse.ExecuteFuncEnum.EXECUTESCRIPTANDRETURNLOGS;
    const isManualVerification = executeFunc === ExecuteFunctionResponse.ExecuteFuncEnum.REQUESTUSERTOMANUALLYVERIFYOUTPUT;
    const isUserAdditionalInfo = executeFunc === ExecuteFunctionResponse.ExecuteFuncEnum.GETUSERADDITIONALINFO;
    const isInitialScriptAndLogs = executeFunc === ExecuteFunctionResponse.ExecuteFuncEnum.GETINITIALSCRIPTANDINITIALLOGS;
    const needsVerification = (isScriptExecution || isManualVerification) &&
        iteration.status === AiScriptChangeIteration.StatusEnum.USERINPUTREQUIRED;
    const alreadyAnswered = iteration.functionCallOutput?.functionCallResult !== null;
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [editedInput, setEditedInput] = React.useState<string>("");

    const toggleScriptPreview = () => {
        const newVisibility = !showScriptPreview;
        setShowScriptPreview(newVisibility);
        scriptPreviewStateCache.set(iterationId, newVisibility);
    };

    function handleEditIteration() {
        if (props.task.cancelledByUser === true) {
            notify.show('This task is canceled and cannot be edited.', 'error', 3000);
            setIsEditing(false);
            return;
        }

        if (!editedInput.trim()) {
            notify.show('Please provide input before saving', 'warning', 3000);
            return;
        }

        setIsSubmitting(true);

        const editRequest = {
            taskId: props.task._id,
            iterationIndex: props.indexNr,
            newFunctionCallResult: editedInput
        };

        editAiAgentTaskIteration(editRequest)
            .then(response => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error(`Failed to update iteration: ${response.statusText}`);
                }
            })
            .then(updatedTask => {
                notify.show('Iteration updated successfully. Restarting AI processing...', 'success', 3000);

                // Now trigger the AI agent with the updated task
                const xReqId = uuid();
                return triggerAiScriptTask(updatedTask, xReqId);
            })
            .then(triggerResponse => {
                if (triggerResponse.ok) {
                    notify.show('AI agent restarted successfully', 'success', 3000);
                    props.onClose(null);
                    setIsSubmitting(false);
                } else {
                    throw new Error(`Failed to restart AI agent: ${triggerResponse.statusText}`);
                }
            })
            .catch(error => {
                notify.show(`Error: ${error.message}`, 'error', 5000);
                console.error("Error updating iteration:", error);
                setIsSubmitting(false);
            });
    }

    // For already answered iterations with script, automatically show the script
    React.useEffect(() => {
        if (props.isOpen && alreadyAnswered && (isScriptExecution || isManualVerification) && script) {
            if (!scriptPreviewStateCache.has(iterationId)) {
                setShowScriptPreview(true);
                scriptPreviewStateCache.set(iterationId, true);
            }
        }
    }, [props.isOpen, alreadyAnswered, isScriptExecution, isManualVerification, script, iterationId]);

    function sendUserInput() {
        if (selectedOption === false && !userInput.trim()) {
            notify.show('Please provide feedback when selecting "No"', 'warning', 3000);
            return;
        }

        if (iteration.status === AiScriptChangeIteration.StatusEnum.USERINPUTREQUIRED && !userInput.trim() && !needsVerification) {
            notify.show('Please provide the requested information', 'warning', 3000);
            return;
        }

        setIsSubmitting(true);

        const task = aiAgentTasks.value.find((a) => {
            return a._id == props.task._id
        }) ;
        let currentIteration = task.iterations[props.indexNr]

        if (!currentIteration.functionCallOutput) {
            currentIteration.functionCallOutput = {} as ExecuteFunctionResponse;
        }

        const isApproved = selectedOption === true;

        if (needsVerification) {
            currentIteration.userApprovedScript = isApproved;
            currentIteration.functionCallOutput.functionCallResult = isApproved ? "Script approved" : userInput;

            if (isApproved) {
                currentIteration.status = AiScriptChangeIteration.StatusEnum.DONE;
                if (currentIteration.functionCallOutput.script) {
                    task.latestUpdatedScript = currentIteration.functionCallOutput.script;
                }
            }
        } else {
            currentIteration.functionCallOutput.functionCallResult = userInput;
        }

        currentIteration.functionCallOutput.executeFunc = executeFunc;

        task.iterations[props.indexNr] = currentIteration;
        const isTaskComplete = isApproved && needsVerification;

        if (needsVerification && isApproved) {
            updateAiAgentTaskDirectly(task)
                .then(response => {
                    notify.show('Script approved successfully', 'success', 3000);
                    props.onClose(null);
                    setIsSubmitting(false);
                })
                .catch(error => {
                    notify.show(`Error: ${error.message}`, 'error', 5000);
                    console.error("Error updating task:", error);
                    setIsSubmitting(false);
                });
        } else {
            const xReqId = uuid();

            triggerAiScriptTask(task, xReqId)
                .then(response => {
                    if (response.ok) {
                        notify.show('Response submitted successfully', 'success', 3000);
                        props.onClose(xReqId);
                        setIsSubmitting(false); // Make sure to reset isSubmitting
                    } else {
                        throw new Error(`Failed to update task: ${response.statusText}`);
                    }
                })
                .catch(error => {
                    notify.show(`Error: ${error.message}`, 'error', 5000);
                    console.error("Error submitting user input:", error);
                    setIsSubmitting(false);
                });
        }
    }


    function renderVerificationSection() {
        const title = isScriptExecution ? "Script Executed Successfully" : "Manual Verification Required";
        const messageType = isScriptExecution ? "success" : "warning";
        const question = isScriptExecution ?
            "Is the script complete and working correctly?" :
            "Is the script correct?";

        return (
            <>
                <Message icon className={messageType}>
                    <Icon name='check circle' />
                    <Message.Content>
                        <Message.Header>{title}</Message.Header>
                        <p>{query}</p>
                    </Message.Content>
                </Message>

                <Button
                    primary
                    onClick={toggleScriptPreview}
                    style={{ marginBottom: '1rem' }}
                >
                    {showScriptPreview ? "Hide Script" : "View Script"}
                </Button>

                {showScriptPreview && (
                    <Segment>
                        <Label>Generated Script</Label>
                        <AceEditor
                            theme="monokai"
                            mode='groovy'
                            value={script}
                            width="100%"
                            height="300px"
                            readOnly={true}
                        />
                    </Segment>
                )}

                {scriptExecutionLogs && (
                    <Segment>
                        <Label>Script Execution Logs</Label>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: scriptExecutionLogs.includes("Exception") || scriptExecutionLogs.includes("Error") ? 'red' : 'inherit' }}>
                            {scriptExecutionLogs}
                        </pre>
                    </Segment>
                )}

                <Form>
                    <Form.Field>
                        <label>{question}</label>
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                            <Button
                                color={selectedOption === true ? "green" : undefined}
                                onClick={() => setSelectedOption(true)}
                                icon
                                labelPosition='left'
                                disabled={props.task.cancelledByUser === true}
                            >
                                <Icon name='check' />
                                Yes, it's correct
                            </Button>
                            <Button
                                color={selectedOption === false ? "red" : undefined}
                                onClick={() => setSelectedOption(false)}
                                icon
                                labelPosition='left'
                                disabled={props.task.cancelledByUser === true}
                            >
                                <Icon name='times' />
                                No, needs changes
                            </Button>
                        </div>
                    </Form.Field>

                    {selectedOption === false && (
                        <Form.Field>
                            <label>Please explain what changes are needed:</label>
                            <AceEditor
                                theme="monokai"
                                mode='text'
                                value={userInput}
                                width="100%"
                                height="200px"
                                onChange={(e) => setUserInput(e)}
                                readOnly={props.task.cancelledByUser === true}
                            />
                        </Form.Field>
                    )}

                    <Button
                        primary
                        onClick={sendUserInput}
                        disabled={selectedOption === null || (selectedOption === false && !userInput.trim()) || isSubmitting || props.task.cancelledByUser === true}
                        loading={isSubmitting}
                        style={{ marginTop: '1rem' }}
                    >
                        Submit Response
                    </Button>
                </Form>
            </>
        );
    }

    function renderUserInputSection() {
        if (alreadyAnswered) {
            return (
                <>
                    <Message info>
                        <Message.Header>Response Already Submitted</Message.Header>
                        <p>The AI requested: <strong>{query}</strong></p>
                        <p>Your response was:</p>
                        <Segment>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {iteration.functionCallOutput?.functionCallResult}
                        </pre>
                        </Segment>

                        {iteration.status === AiScriptChangeIteration.StatusEnum.USERINPUTREQUIRED && (
                            <div style={{ marginTop: '1rem' }}>
                                {!isEditing ? (
                                    <>
                                        <Button
                                            color="red"
                                            onClick={() => {
                                                if (props.task.cancelledByUser === true) return;
                                                setIsEditing(true);
                                                setEditedInput(iteration.functionCallOutput?.functionCallResult || ""); // Pre-fill
                                            }}
                                            icon
                                            labelPosition='left'
                                            disabled={props.task.cancelledByUser === true} // Makes button grey and non-clickable
                                        >
                                            <Icon name='edit' />
                                            Edit Response
                                        </Button>

                                        {/* Display message if task is canceled, shown alongside the (disabled) button */}
                                        {props.task.cancelledByUser === true && (
                                            <Message warning icon style={{ marginTop: '10px' }}>
                                                <Icon name='ban' />
                                                <Message.Content>
                                                    <Message.Header>Task Canceled</Message.Header>
                                                    <p>This task has been canceled and responses cannot be edited.</p>
                                                </Message.Content>
                                            </Message>
                                        )}
                                    </>
                                ) : (
                                    <div>
                                        {props.task.cancelledByUser === true && (
                                            <Message error icon>
                                                <Icon name='ban' />
                                                <Message.Content>
                                                    <Message.Header>Cannot Edit Canceled Task</Message.Header>
                                                    <p>This task is canceled. Changes will not be saved. Please cancel editing.</p>
                                                </Message.Content>
                                            </Message>
                                        )}
                                        <Message error>
                                            <Message.Header>Edit Mode</Message.Header>
                                            <p>WARNING: Editing this response will remove all subsequent iterations and restart the TriggerAiAgentTask from this point. The latest successfully running script Might Also Change</p>
                                        </Message>

                                        <AceEditor
                                            theme="monokai"
                                            mode='text'
                                            value={editedInput}
                                            width="100%"
                                            height="200px"
                                            onChange={(e) => setEditedInput(e)}
                                            placeholder="Edit your response here..."
                                            readOnly={props.task.cancelledByUser === true} // Make editor readonly if canceled
                                        />

                                        <div style={{ marginTop: '1rem' }}>
                                            <Button
                                                primary
                                                onClick={handleEditIteration}
                                                disabled={!editedInput.trim() || isSubmitting || props.task.cancelledByUser === true} // Disable save if canceled
                                                loading={isSubmitting}
                                                icon
                                                labelPosition='left'
                                            >
                                                <Icon name='save' />
                                                Save & Trigger AI Agent Task
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setIsEditing(false);
                                                    setEditedInput("");
                                                }}
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Message>

                    {(isScriptExecution || isManualVerification) && script && (
                        <>
                            <Button
                                primary
                                onClick={toggleScriptPreview}
                                style={{ marginBottom: '1rem' }}
                            >
                                {showScriptPreview ? "Hide Script" : "View Script"}
                            </Button>

                            {showScriptPreview && (
                                <Segment>
                                    <Label>Generated Script</Label>
                                    <AceEditor
                                        theme="monokai"
                                        mode='groovy'
                                        value={script}
                                        width="100%"
                                        height="60vh" // MODIFIED from "300px"
                                        readOnly={true}
                                    />
                                </Segment>
                            )}
                        </>
                    )}

                    {scriptExecutionLogs && (
                        <Segment>
                            <Label>Script Execution Logs</Label>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: scriptExecutionLogs.includes("Exception") || scriptExecutionLogs.includes("Error") ? 'red' : 'inherit' }}>
                            {scriptExecutionLogs}
                        </pre>
                        </Segment>
                    )}
                </>
            );
        }

        if (iteration.status === AiScriptChangeIteration.StatusEnum.USERINPUTREQUIRED) {
            // If task is canceled, show a message here too and prevent new input forms
            if (props.task.cancelledByUser === true) {
                return (
                    <Message warning icon>
                        <Icon name='ban' />
                        <Message.Content>
                            <Message.Header>Task Canceled</Message.Header>
                            <p>This task has been canceled by the user. No further input can be provided.</p>
                            {query && <p>The AI originally requested: <strong>{query}</strong></p>}
                        </Message.Content>
                    </Message>
                );
            }
            const isRepetitionDetection = query && query.includes("Repetitive pattern detected");

            if (isRepetitionDetection) {
                return (
                    <>
                        <Message warning icon>
                            <Icon name='warning circle' />
                            <Message.Content>
                                <Message.Header>Repetitive Behavior Detected</Message.Header>
                                <p>{query}</p>
                            </Message.Content>
                        </Message>

                        <AceEditor
                            theme="monokai"
                            mode='text'
                            value={userInput}
                            width="100%"
                            height="300px"
                            onChange={(e) => setUserInput(e)}
                            placeholder="Please provide additional guidance to help the AI make progress..."
                        />

                        <Button
                            primary
                            onClick={sendUserInput}
                            disabled={!userInput.trim() || isSubmitting}
                            loading={isSubmitting}
                            style={{ marginTop: '1rem' }}
                        >
                            Send Guidance
                        </Button>
                    </>
                );
            }

            // Handle verification cases
            if (needsVerification) {
                return renderVerificationSection();
            }
            // Handle all other USER_INPUT_REQUIRED cases
            return (
                <>
                    <Message info icon>
                        <Icon name='info circle' />
                        <Message.Content>
                            <Message.Header>Input Required</Message.Header>
                            <p>{query}</p>
                        </Message.Content>
                    </Message>

                    {/* Show script if available (for any function type) */}
                    {script && (
                        <>
                            <Button
                                primary
                                onClick={toggleScriptPreview}
                                style={{ marginBottom: '1rem' }}
                            >
                                {showScriptPreview ? "Hide Script" : "View Script"}
                            </Button>

                            {showScriptPreview && (
                                <Segment>
                                    <Label>Script</Label>
                                    <AceEditor
                                        theme="monokai"
                                        mode='groovy'
                                        value={script}
                                        width="100%"
                                        height="60vh" // MODIFIED from "300px"
                                        readOnly={true}
                                    />
                                </Segment>
                            )}
                        </>
                    )}
                    {scriptExecutionLogs && (
                        <Segment>
                            <Label>Execution Logs</Label>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: scriptExecutionLogs.includes("Exception") || scriptExecutionLogs.includes("Error") ? 'red' : 'inherit' }}>
                            {scriptExecutionLogs}
                        </pre>
                        </Segment>
                    )}

                    <AceEditor
                        theme="monokai"
                        mode='text'
                        value={userInput}
                        width="100%"
                        height="300px"
                        onChange={(e) => setUserInput(e)}
                        placeholder="Type your response here..."
                    />

                    <Button
                        primary
                        onClick={sendUserInput}
                        disabled={!userInput.trim() || isSubmitting}
                        loading={isSubmitting}
                        style={{ marginTop: '1rem' }}
                    >
                        Send Response
                    </Button>
                </>
            );
        }

        // The rest of the original function for other cases
        if (isInitialScriptAndLogs && iteration.status === AiScriptChangeIteration.StatusEnum.RUNNING) {
            const hasLogs = !!(scriptExecutionLogs && scriptExecutionLogs.trim());

            return (
                <>
                    <Message icon className={hasLogs ? "error" : "success"}>
                        <Icon name={hasLogs ? 'warning sign' : 'check circle'} />
                        <Message.Content>
                            <Message.Header>
                                {hasLogs ? "Initial Script Execution Failed" : "Initial Script Executed Successfully"}
                            </Message.Header>
                            <p>
                                {hasLogs
                                    ? "The initial script execution produced errors or exceptions."
                                    : "The initial script executed without any errors or exceptions. - a conversion Result File has been Added!"}
                            </p>
                        </Message.Content>
                    </Message>

                    {hasLogs ? (
                        <Segment>
                            <Label>Execution Errors</Label>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'red'
                            }}>
                    {scriptExecutionLogs}
                </pre>
                        </Segment>
                    ) : (
                        <Message success>
                            <p>Script executed correctly. No logs were produced.</p>
                        </Message>
                    )}
                </>
            );
        }

        if (isScriptExecution && iteration.status === AiScriptChangeIteration.StatusEnum.RUNNING) {
            const [isDarkTheme, setIsDarkTheme] = React.useState<boolean>(true);
            const isUnchangedScript = iteration.userApprovedScript === false &&
                (!scriptExecutionLogs || scriptExecutionLogs.trim() === "") &&
                iteration.userInputPrompt &&
                iteration.functionCallOutput.script == props.task.inputScript

            if (isUnchangedScript) {
                return (
                    <>
                        <Message warning icon>
                            <Icon name='refresh' />
                            <Message.Content>
                                <Message.Header>Script Unchanged - AI Continuing</Message.Header>
                                <p>The AI returned the same script without modifications. The system has automatically requested improvements and the AI is continuing to work on it.</p>
                            </Message.Content>
                        </Message>

                        {showScriptPreview && (
                            <Segment>
                                <Label>Unchanged Script (Same as Original)</Label>
                                <AceEditor
                                    theme="monokai"
                                    mode='groovy'
                                    value={script}
                                    width="100%"
                                    height="60vh"
                                    readOnly={true}
                                    setOptions={{ useWorker: false }}
                                />
                            </Segment>
                        )}

                        <Message info>
                            <Message.Header>System Response</Message.Header>
                            <p>The system automatically sent this message to the AI:</p>
                            <Segment style={{ backgroundColor: '#f8f9fa', fontStyle: 'italic' }}>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.9em' }}>
                            {iteration.userInputPrompt}
                        </pre>
                            </Segment>
                        </Message>

                    </>
                );
            }

            const copyScriptToClipboard = () => {
                if (script) {
                    navigator.clipboard.writeText(script)
                        .then(() => {
                            console.log('Script copied to clipboard');
                        })
                        .catch(err => {
                            console.error('Failed to copy script: ', err);
                        });
                }
            };

            const toggleTheme = () => {
                setIsDarkTheme(!isDarkTheme);
            };

            const currentTheme = isDarkTheme ? "monokai" : "github";

            return (
                <>
                    <Message error icon>
                        <Icon name='warning sign' />
                        <Message.Content>
                            <Message.Header>Script Execution Errors</Message.Header>
                            <p>The script was executed but encountered errors. The AI will automatically continue to fix these issues.</p>
                        </Message.Content>
                    </Message>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <Button
                            primary
                            onClick={toggleScriptPreview}
                        >
                            {showScriptPreview ? "Hide Script" : "View Script"}
                        </Button>

                        {showScriptPreview && (
                            <div>
                                <Button
                                    size="small"
                                    icon
                                    onClick={toggleTheme}
                                    title={isDarkTheme ? "Switch to light theme" : "Switch to dark theme"}
                                >
                                    <Icon name={isDarkTheme ? "sun" : "moon"} />
                                </Button>
                                <Button
                                    size="small"
                                    icon
                                    onClick={copyScriptToClipboard}
                                    title="Copy script to clipboard"
                                >
                                    <Icon name="copy" />
                                </Button>
                            </div>
                        )}
                    </div>

                    {showScriptPreview && (
                        <Segment>
                            <Label>Generated Script</Label>
                            <AceEditor
                                theme={currentTheme}
                                mode='groovy'
                                value={script}
                                width="100%"
                                height="60vh" // MODIFIED from "300px"
                                readOnly={true}
                                setOptions={{ useWorker: false }}
                            />
                        </Segment>
                    )}

                    <Segment>
                        <Label>Execution Logs (Errors)</Label>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'red' }}>
                        {scriptExecutionLogs}
                    </pre>
                    </Segment>
                </>
            );
        }

        return (
            <Message>
                <Message.Header>No User Input Required</Message.Header>
                <p>This iteration does not require any user input.</p>

                {scriptExecutionLogs && (
                    <Segment>
                        <Label>Script Execution Logs</Label>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: scriptExecutionLogs.includes("Exception") || scriptExecutionLogs.includes("Error") ? 'red' : 'inherit' }}>
                        {scriptExecutionLogs}
                    </pre>
                    </Segment>
                )}
            </Message>
        );
    }

    function renderIterationDetails() {
        const tokens = iteration.aiModelResponseScripting?.totalUsageTokensNumber || 0;
        const completionTokens = iteration.aiModelResponseScripting?.totalCompletionTokensNumber || 0;
        const price = iteration.aiModelResponseScripting?.totalPrice || 0;
        const duration = iteration.durationMs || 0;
        const status = iteration.status;
        const funcType = formatExecuteFunc(executeFunc);
        const documentationType = iteration.aiModelResponseScripting?.aiModelResponse?.documentationType;
        const isGetDocumentation = executeFunc === ExecuteFunctionResponse.ExecuteFuncEnum.GETDOCUMENTATION;

        return (
            <Segment>
                <Grid columns={2}>
                    <Grid.Row>
                        <Grid.Column>
                            <Label color={getStatusColor(status)}>Status: {status}</Label>
                            <Label color="blue">Type: {funcType}</Label>
                            {isGetDocumentation && documentationType && (
                                <Label color="teal">Documentation Type: {documentationType}</Label>
                            )}
                            <Label>Created: {new Date(iteration.timestamp).toLocaleString()}</Label>
                            {tokens > 0 && <Label>Usage Tokens: {tokens}</Label>}
                            {completionTokens > 0 && <Label>Completion Tokens: {completionTokens}</Label>}
                            {(tokens > 0 || completionTokens > 0) && <Label>Total Tokens: {tokens + completionTokens}</Label>}
                            {price > 0 && <Label>Price: ${price.toFixed(6)}</Label>}
                            {duration > 0 && <Label>Duration: {(duration / 1000).toFixed(2)}s</Label>}
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Segment>
        );
    }

    function getStatusColor(status: string) {
        switch (status) {
            case "DONE": return "green";
            case "USERINPUTREQUIRED": return "orange";
            case "RUNNING": return "blue";
            case "ABORTED": return "red";
            default: return "grey";
        }
    }

    function formatExecuteFunc(executeFunc: string) {
        if (!executeFunc) return "Unknown";
        return executeFunc
            .split('_')
            .map(word => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
    }

    return (
        <Modal
            open={props.isOpen}
            onClose={() => props.onClose(null)}
            closeIcon
            size={"fullscreen"}
            className={"AiAgentTaskIterationModal"}
        >
            <Modal.Header>
                <Icon name='code branch' /> Iteration {props.indexNr + 1}
                {props.task.cancelledByUser === true && (
                    <Label color="red" style={{ marginLeft: '15px', verticalAlign: 'middle' }}> {/* Changed color to grey */}
                        <Icon name="ban" /> Task Canceled
                    </Label>
                )}
            </Modal.Header>
            <Modal.Content scrolling>
                {renderIterationDetails()}
                <Divider />
                {renderUserInputSection()}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => props.onClose(null)}>Close</Button>
            </Modal.Actions>
        </Modal>
    );
}